// Chemical calculation constants and target ranges for TFPLog
// Using TFP (Trouble Free Pool) methodology for accurate calculations

// TFP chemical constants for accurate dosing calculations
// These constants are from the official TFP PoolMath calculator
export const TFP_CHEMICAL_CONSTANTS = {
  // Liquid chlorine/bleach constants based on concentration
  // Formula used by TFP: 75.71 * concentration + 0.746 * concentration^2
  bleach_6: 75.71 * 6 + 0.746 * 36,         // ≈ 481.1
  bleach_10: 75.71 * 10 + 0.746 * 100,      // ≈ 831.7
  bleach_12: 75.71 * 12 + 0.746 * 144,      // ≈ 1016.1
  bleach_12_5: 75.71 * 12.5 + 0.746 * 156.25, // ≈ 1062.8
  
  // Solid chlorine constants
  cal_hypo_65: 5422.41,
  cal_hypo_73: 4698.63,
  trichlor: 6854.95,
  dichlor: 4149.03
}

// Target ranges for different parameters
export const TARGET_RANGES = {
  // Free chlorine is CYA-dependent (calculated dynamically)
  FREE_CHLORINE: {
    min_multiplier: 0.075, // 7.5% of CYA (minimum)
    target_multiplier: 0.12, // 12% of CYA (target)
    shock_multiplier: 0.40,  // 40% of CYA (shock level)
    absolute_min: 2.0,     // Never go below 2 ppm
    absolute_max: 15.0     // Never exceed 15 ppm for safety
  },
  
  // Combined chlorine should always be low
  COMBINED_CHLORINE: {
    min: 0,
    max: 0.5,
    ideal_max: 0.2
  },
  
  // pH ranges - Updated for TFP liquid chlorine methodology
  PH: {
    min: 7.2,        // Acceptable minimum
    max: 7.8,        // TFP recommended max for liquid chlorine users
    ideal: 7.6       // TFP recommended target for liquid chlorine users
  },
  
  // Total Alkalinity - Updated for TFP liquid chlorine methodology
  TOTAL_ALKALINITY: {
    min: 60,         // TFP recommended minimum for liquid chlorine
    max: 90,         // TFP recommended maximum for liquid chlorine
    ideal: 80        // TFP recommended target for liquid chlorine
  },
  
  // CYA/Stabilizer
  CYA_STABILIZER: {
    min: 30,
    max: 90,
    ideal: 40
  },
  
  // Calcium Hardness (varies by pool type)
  CALCIUM_HARDNESS: {
    vinyl_liner: { min: 150, max: 300, ideal: 200 },
    plaster: { min: 200, max: 400, ideal: 300 },
    fiberglass: { min: 150, max: 300, ideal: 200 }
  },
  
  // Water Temperature (informational)
  WATER_TEMPERATURE: {
    min: 45,
    max: 104,
    ideal: 78
  }
}

// Chemical types with calculation data
export const CHEMICAL_DATA = {
  // Sanitizers - Updated with TFP constants
  'liquid_chlorine_10': {
    name: 'Liquid Chlorine 10%',
    concentration: 10.0, // % available chlorine
    density: 8.25, // lbs per gallon
    unit: 'gallon',
    unitType: 'volume',
    affects: ['free_chlorine'],
    tfp_constant: 831.7 // TFP constant for 10% concentration
  },
  
  'liquid_chlorine_12': {
    name: 'Liquid Chlorine 12%',
    concentration: 12.0, // % available chlorine
    density: 8.25, // lbs per gallon
    unit: 'gallon',
    unitType: 'volume',
    affects: ['free_chlorine'],
    tfp_constant: 1016.1 // TFP constant for 12% concentration
  },
  
  'liquid_chlorine_12_5': {
    name: 'Liquid Chlorine 12.5%',
    concentration: 12.5, // % available chlorine
    density: 8.25, // lbs per gallon
    unit: 'gallon',
    unitType: 'volume',
    affects: ['free_chlorine'],
    tfp_constant: 1062.8 // TFP constant for 12.5% concentration
  },
  
  'household_bleach_6': {
    name: 'Household Bleach 6%',
    concentration: 6.0, // % available chlorine
    density: 8.25, // lbs per gallon
    unit: 'gallon',
    unitType: 'volume',
    affects: ['free_chlorine'],
    tfp_constant: 481.1 // TFP constant for 6% concentration
  },
  
  'cal_hypo_65': {
    name: 'Cal-Hypo 65%',
    concentration: 65, // % available chlorine
    unit: 'pound',
    unitType: 'weight',
    affects: ['free_chlorine', 'calcium_hardness'],
    tfp_constant: 5422.41, // TFP constant for 65% cal-hypo
    calcium_increase: 7.0 // ppm CH per 10 ppm FC
  },
  
  'cal_hypo_73': {
    name: 'Cal-Hypo 73%',
    concentration: 73, // % available chlorine
    unit: 'pound',
    unitType: 'weight',
    affects: ['free_chlorine', 'calcium_hardness'],
    tfp_constant: 4698.63, // TFP constant for 73% cal-hypo
    calcium_increase: 7.0 // ppm CH per 10 ppm FC
  },
  
  'trichlor': {
    name: 'TriChlor',
    concentration: 90,
    unit: 'pound',
    unitType: 'weight',
    affects: ['free_chlorine', 'cya_stabilizer', 'ph'],
    tfp_constant: 6854.95, // TFP constant for trichlor
    cya_increase: 6.0, // ppm CYA per 10 ppm FC
    ph_decrease_per_lb_per_10k_gal: 0.1 // acidic
  },
  
  'dichlor': {
    name: 'Dichlor',
    concentration: 56,
    unit: 'pound',
    unitType: 'weight',
    affects: ['free_chlorine', 'cya_stabilizer'],
    tfp_constant: 3565.44, // TFP constant for dichlor (updated from PoolMath)
    cya_increase: 9.0 // ppm CYA per 10 ppm FC
  },
  
  // pH Adjusters
  'soda_ash': {
    name: 'Soda Ash',
    unit: 'pound',
    unitType: 'weight',
    affects: ['ph', 'total_alkalinity'],
    ph_increase_per_lb_per_10k_gal: 0.2,
    ta_increase_per_lb_per_10k_gal: 5.0
  },
  
  'borax': {
    name: 'Borax (20 Mule Team)',
    unit: 'pound',
    unitType: 'weight',
    affects: ['ph', 'total_alkalinity'],
    ph_increase_per_lb_per_10k_gal: 0.15,
    ta_increase_per_lb_per_10k_gal: 3.5
  },
  
  'muriatic_acid_31': {
    name: 'Muriatic Acid 31.45%',
    concentration: 31.45, // % HCl (full strength)
    unit: 'fluid_ounce',
    unitType: 'volume',
    affects: ['ph', 'total_alkalinity']
  },
  
  'muriatic_acid_12': {
    name: 'Muriatic Acid 12.5%',
    concentration: 12.5, // % HCl (diluted)
    unit: 'fluid_ounce',
    unitType: 'volume',
    affects: ['ph', 'total_alkalinity']
  },
  
  // Total Alkalinity
  'baking_soda': {
    name: 'Baking Soda',
    unit: 'pound',
    unitType: 'weight',
    affects: ['total_alkalinity'],
    ta_increase_per_lb_per_10k_gal: 6.7, // 1.5 lbs raises 10 ppm
    ph_increase_per_lb_per_10k_gal: 0.05 // minimal pH effect
  },
  
  // CYA/Stabilizer
  'dry_stabilizer': {
    name: 'Dry Stabilizer (CYA)',
    unit: 'pound',
    unitType: 'weight',
    affects: ['cya_stabilizer'],
    cya_increase_per_lb_per_10k_gal: 7.7 // 1.3 lbs raises 10 ppm
  },
  
  'liquid_stabilizer': {
    name: 'Liquid Stabilizer',
    concentration: 15, // % CYA
    unit: 'gallon',
    unitType: 'volume',
    affects: ['cya_stabilizer'],
    cya_increase_per_gal_per_10k_gal: 13.0
  },
  
  // Calcium Hardness
  'calcium_chloride': {
    name: 'Calcium Chloride',
    unit: 'pound',
    unitType: 'weight',
    affects: ['calcium_hardness'],
    calcium_increase_per_lb_per_10k_gal: 8.0 // 1.25 lbs raises 10 ppm
  }
}

// Status levels for color coding
export const STATUS_LEVELS = {
  CRITICAL_LOW: 'critical_low',    // Red - immediate action needed
  LOW: 'low',                      // Orange - should adjust
  GOOD: 'good',                    // Green - in target range
  HIGH: 'high',                    // Orange - should adjust
  CRITICAL_HIGH: 'critical_high',  // Red - immediate action needed
  UNKNOWN: 'unknown'               // Gray - no measurement data
}

// Critical threshold configuration
export const CRITICAL_THRESHOLD_MULTIPLIER = 0.7

// Dosing preferences (conservative by default)
export const DOSING_PREFERENCES = {
  target_percentage: 0.7, // Aim for 70% of the way to ideal
  max_single_dose: {
    free_chlorine: 5.0,     // Don't add more than 5 ppm FC at once
    ph_increase: 0.3,       // Don't raise pH more than 0.3 at once
    ph_decrease: 0.3,       // Don't lower pH more than 0.3 at once
    total_alkalinity: 30,   // Don't change TA more than 30 ppm at once
    cya_stabilizer: 20,     // Don't add more than 20 ppm CYA at once
    calcium_hardness: 50    // Don't add more than 50 ppm CH at once
  }
}

// Common conversion factors
export const CONVERSIONS = {
  gallons_per_10k: 10000,
  oz_per_gallon: 128,
  oz_per_pound: 16,
  ml_per_oz: 29.5735,
  grams_per_oz: 28.3495
}

// Muriatic acid dosing lookup table (ounces of 12.5% acid per 10,000 gallons)
export const ACID_DOSING_TABLE = {
  // pH reduction from X to Y, based on Total Alkalinity
  // Format: [fromPH][toPH][TA_range] = ounces per 10k gallons
  '7.8_7.4': {
    80: 8,   // TA 80 ppm
    100: 10, // TA 100 ppm  
    120: 12, // TA 120 ppm
    150: 15  // TA 150+ ppm
  },
  '7.6_7.2': {
    80: 6,
    100: 8,
    120: 10,
    150: 12
  },
  '7.5_7.2': {
    80: 4,
    100: 6,
    120: 8,
    150: 10
  },
  '7.4_7.2': {
    80: 2,
    100: 3,
    120: 4,
    150: 5
  }
}

// Helper function to interpolate acid dosing based on pH change and TA
export const calculateAcidDosing = (currentPH, targetPH, totalAlkalinity) => {
  // Acid only lowers pH — if target >= current, no acid needed
  if (targetPH >= currentPH) return 0

  const phDrop = Math.round((currentPH - targetPH) * 10) / 10
  
  // Find closest TA bracket
  let taBracket = 100 // default
  if (totalAlkalinity <= 90) taBracket = 80
  else if (totalAlkalinity <= 110) taBracket = 100
  else if (totalAlkalinity <= 135) taBracket = 120
  else taBracket = 150
  
  // Calculate base dosing for common pH drops
  let baseOunces = 0
  
  if (phDrop >= 0.4) {
    // 7.8 to 7.4 (or similar 0.4 drop)
    baseOunces = ACID_DOSING_TABLE['7.8_7.4'][taBracket] || 10
  } else if (phDrop >= 0.3) {
    // 7.5 to 7.2 (or similar 0.3 drop)  
    baseOunces = ACID_DOSING_TABLE['7.5_7.2'][taBracket] || 6
  } else if (phDrop >= 0.2) {
    // 7.4 to 7.2 (or similar 0.2 drop)
    baseOunces = ACID_DOSING_TABLE['7.4_7.2'][taBracket] || 3
  } else {
    // Small adjustments - proportional
    baseOunces = (ACID_DOSING_TABLE['7.4_7.2'][taBracket] || 3) * (phDrop / 0.2)
  }
  
  return Math.max(baseOunces, 1) // Minimum 1 ounce
}

// ── Shared parameter definitions ──────────────────────────────────────────────
// Single source of truth for pool chemistry parameter metadata.
// Components can extend with display-specific fields (icons, colors, etc.)

export const PARAMETER_DEFINITIONS = [
  { key: 'free_chlorine', label: 'Free Chlorine', shortLabel: 'FC', unit: 'ppm', step: '0.25', min: '0', max: '100', category: 'Chlorine' },
  { key: 'combined_chlorine', label: 'Combined Chlorine', shortLabel: 'CC', unit: 'ppm', step: '0.25', min: '0', max: '100', category: 'Chlorine' },
  { key: 'ph', label: 'pH', shortLabel: 'pH', unit: '', step: '0.2', min: '6.0', max: '20', category: 'Balance' },
  { key: 'total_alkalinity', label: 'Total Alkalinity', shortLabel: 'TA', unit: 'ppm', step: '1', min: '0', max: '10000', category: 'Balance' },
  { key: 'cya_stabilizer', label: 'CYA/Stabilizer', shortLabel: 'CYA', unit: 'ppm', step: '1', min: '0', max: '1000', category: 'Balance' },
  { key: 'calcium_hardness', label: 'Calcium Hardness', shortLabel: 'CH', unit: 'ppm', step: '1', min: '0', max: '10000', category: 'Balance' },
  { key: 'water_temperature', label: 'Water Temperature', shortLabel: 'Temp', unit: '°F', step: '1', min: '32', max: '120', category: 'Environmental' },
]

// ── Chemical category color mapping ───────────────────────────────────────────

export const CATEGORY_COLORS = {
  sanitizer: 'success',
  ph_adjuster: 'primary',
  alkalinity: 'secondary',
  cya: 'warning',
  calcium: 'info',
}

export const getCategoryColor = (category) => {
  return CATEGORY_COLORS[category] || 'default'
}

// ── Relative date formatting ──────────────────────────────────────────────────

export const formatRelativeDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now - date
  const diffInHours = diffInMs / 36e5

  if (diffInMs < 0) {
    return date.toLocaleDateString()
  } else if (diffInHours < 24) {
    const hours = Math.round(diffInHours)
    return `${date.toLocaleTimeString()} ${hours < 1 ? '(Just now)' : `(${hours}h ago)`}`
  } else if (diffInHours < 168) { // 7 days
    const days = Math.round(diffInHours / 24)
    return `${date.toLocaleDateString()} (${days} day${days > 1 ? 's' : ''} ago)`
  } else {
    return date.toLocaleDateString()
  }
}

// ── Supabase query constants ──────────────────────────────────────────────────

export const CHEMICAL_ADDITIONS_SELECT = `
  *,
  chemical_types (
    name,
    category,
    concentration_percent,
    default_unit
  )
`